(() => {
  const SELECTOR = '[data-ct-reveal]';
  const MASK_CLASS = 'ct-reveal-mask';
  const LINE_CLASS = 'ct-reveal-line';
  const MEASURE_CLASS = 'ct-reveal-measure';

  const LINE_TOP_TOLERANCE = 2;
  const LINE_DELAY_STEP = 110;

  let observer = null;
  let resizeTimer = null;
  let dynamicRevealObserver = null;
  let fontsRefreshDone = false;

  function buildObserver() {
    if (observer || !('IntersectionObserver' in window)) return;

    observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;

          entry.target.classList.add('is-in-view');
          observer.unobserve(entry.target);
        });
      },
      {
        threshold: 0.15,
        rootMargin: '0px 0px -10% 0px'
      }
    );
  }

  function isStackManaged(element) {
    return element.getAttribute('data-ct-reveal-managed') === 'stack';
  }

  function getSourceHtml(element) {
    if (!element.dataset.ctRevealSource) {
      element.dataset.ctRevealSource = element.innerHTML;
    }

    return element.dataset.ctRevealSource;
  }

  function normaliseText(text) {
    return (text || '')
      .replace(/\u00a0/g, ' ')
      .replace(/\r\n/g, '\n')
      .replace(/\r/g, '\n');
  }

  function getSourceText(element) {
    const temp = document.createElement('div');

    temp.innerHTML = getSourceHtml(element).replace(/<br\s*\/?>/gi, '\n');

    return normaliseText(temp.innerText || temp.textContent || '');
  }

  function getParagraphs(element) {
    return getSourceText(element)
      .split(/\n+/)
      .map((item) => item.replace(/\s+/g, ' ').trim())
      .filter(Boolean);
  }

  function getContentWidth(element) {
    const computed = window.getComputedStyle(element);
    const computedWidth = parseFloat(computed.width);

    if (computedWidth && computedWidth > 0) {
      return computedWidth;
    }

    const rect = element.getBoundingClientRect();

    if (rect.width && rect.width > 0) {
      return rect.width;
    }

    if (element.parentElement) {
      const parentRect = element.parentElement.getBoundingClientRect();

      if (parentRect.width && parentRect.width > 0) {
        return parentRect.width;
      }
    }

    return window.innerWidth;
  }

  function copyTextStyles(source, target) {
    const computed = window.getComputedStyle(source);

    const properties = [
      'font-family',
      'font-size',
      'font-style',
      'font-weight',
      'font-stretch',
      'font-variant',
      'font-kerning',
      'font-feature-settings',
      'font-variation-settings',
      'letter-spacing',
      'line-height',
      'text-transform',
      'text-indent',
      'text-align',
      'text-rendering',
      'white-space',
      'word-spacing',
      'word-break',
      'overflow-wrap',
      'hyphens',
      'text-wrap'
    ];

    properties.forEach((property) => {
      const value = computed.getPropertyValue(property);

      if (value) {
        target.style.setProperty(property, value);
      }
    });

    target.style.boxSizing = 'content-box';
    target.style.margin = '0';
    target.style.padding = '0';
    target.style.border = '0';
  }

  function getWordRanges(text) {
    const ranges = [];
    const regex = /\S+/g;
    let match;

    while ((match = regex.exec(text)) !== null) {
      ranges.push({
        start: match.index,
        end: match.index + match[0].length,
        text: match[0]
      });
    }

    return ranges;
  }

  function getRangeTop(textNode, start, end) {
    const range = document.createRange();

    range.setStart(textNode, start);
    range.setEnd(textNode, end);

    const rects = Array.from(range.getClientRects()).filter((rect) => {
      return rect.height > 0;
    });

    if (typeof range.detach === 'function') {
      range.detach();
    }

    if (!rects.length) return null;

    return Math.round(rects[0].top);
  }

  function measureLines(element, paragraphText) {
    const text = paragraphText.replace(/\s+/g, ' ').trim();

    if (!text) return [];
    if (!document.createRange) return [text];

    const width = getContentWidth(element);

    if (!width || width <= 0) return [text];

    const measure = document.createElement(element.tagName.toLowerCase());

    measure.className = element.className;
    measure.classList.add(MEASURE_CLASS);
    measure.setAttribute('aria-hidden', 'true');
    measure.textContent = text;

    copyTextStyles(element, measure);

    measure.style.position = 'absolute';
    measure.style.visibility = 'hidden';
    measure.style.pointerEvents = 'none';
    measure.style.left = '-99999px';
    measure.style.top = '0';
    measure.style.zIndex = '-1';
    measure.style.display = 'block';
    measure.style.width = `${Math.ceil(width)}px`;
    measure.style.maxWidth = `${Math.ceil(width)}px`;
    measure.style.minWidth = '0';
    measure.style.height = 'auto';
    measure.style.overflow = 'visible';

    document.body.appendChild(measure);

    const textNode = measure.firstChild;
    const wordRanges = getWordRanges(text);
    const lines = [];

    let currentTop = null;
    let currentWords = [];

    if (!textNode || !wordRanges.length) {
      measure.remove();
      return [text];
    }

    wordRanges.forEach((word) => {
      const top = getRangeTop(textNode, word.start, word.end);

      if (top === null) {
        currentWords.push(word.text);
        return;
      }

      if (currentTop === null) {
        currentTop = top;
        currentWords.push(word.text);
        return;
      }

      if (Math.abs(top - currentTop) <= LINE_TOP_TOLERANCE) {
        currentWords.push(word.text);
        return;
      }

      if (currentWords.length) {
        lines.push(currentWords.join(' '));
      }

      currentTop = top;
      currentWords = [word.text];
    });

    if (currentWords.length) {
      lines.push(currentWords.join(' '));
    }

    measure.remove();

    return lines.length ? lines : [text];
  }

  function buildBlock(element) {
    const html = getSourceHtml(element);
    const baseDelay = parseInt(element.dataset.ctRevealDelay || '0', 10) || 0;

    element.innerHTML = '';

    const mask = document.createElement('span');
    mask.className = MASK_CLASS;

    const inner = document.createElement('span');
    inner.className = LINE_CLASS;
    inner.innerHTML = html;
    inner.style.transitionDelay = `${baseDelay}ms`;

    mask.appendChild(inner);
    element.appendChild(mask);
  }

  function buildLines(element) {
    const paragraphs = getParagraphs(element);

    if (!paragraphs.length) return;

    const baseDelay = parseInt(element.dataset.ctRevealDelay || '0', 10) || 0;
    const finalLines = [];

    paragraphs.forEach((paragraph, paragraphIndex) => {
      const lines = measureLines(element, paragraph);

      lines.forEach((line) => {
        finalLines.push(line);
      });

      if (paragraphIndex < paragraphs.length - 1) {
        finalLines.push('__BREAK__');
      }
    });

    element.innerHTML = '';

    let lineIndex = 0;

    finalLines.forEach((line) => {
      if (line === '__BREAK__') {
        element.appendChild(document.createElement('br'));
        return;
      }

      const mask = document.createElement('span');
      mask.className = MASK_CLASS;

      const inner = document.createElement('span');
      inner.className = LINE_CLASS;
      inner.textContent = line;
      inner.style.transitionDelay = `${baseDelay + lineIndex * LINE_DELAY_STEP}ms`;

      mask.appendChild(inner);
      element.appendChild(mask);

      lineIndex += 1;
    });
  }

  function prepareElement(element) {
    const alreadyVisible = element.classList.contains('is-in-view');
    const mode = element.dataset.ctRevealMode || 'lines';

    getSourceHtml(element);

    if (mode === 'block') {
      buildBlock(element);
    } else {
      buildLines(element);
    }

    if (alreadyVisible) {
      element.classList.add('is-in-view');
    } else {
      element.classList.remove('is-in-view');
    }
  }

  function getScopeElements(scope = document) {
    if (!scope) return [];

    if (scope.matches && scope.matches(SELECTOR)) {
      return [scope];
    }

    if (scope.querySelectorAll) {
      return Array.from(scope.querySelectorAll(SELECTOR));
    }

    return [];
  }

  function initReveal(scope = document, options = {}) {
    const force = options.force || false;
    const elements = getScopeElements(scope);

    if (!elements.length) return;

    buildObserver();

    elements.forEach((element) => {
      const managedByStack = isStackManaged(element);

      if (managedByStack && !force) {
        return;
      }

      prepareElement(element);

      if (!('IntersectionObserver' in window)) {
        if (!managedByStack) {
          window.requestAnimationFrame(() => {
            element.classList.add('is-in-view');
          });
        }

        return;
      }

      observer.unobserve(element);

      if (!element.classList.contains('is-in-view') && !managedByStack) {
        observer.observe(element);
      }
    });
  }

  function refreshRevealScope(scope = document) {
    const elements = getScopeElements(scope);

    if (!elements.length) return;

    elements.forEach((element) => {
      if (element.dataset.ctRevealSource) {
        element.innerHTML = element.dataset.ctRevealSource;
      }

      element.classList.remove('is-in-view');
      prepareElement(element);

      if (observer) {
        observer.unobserve(element);
      }
    });
  }

  function rebuildAll() {
    const elements = Array.from(document.querySelectorAll(SELECTOR));

    if (!elements.length) return;

    elements.forEach((element) => {
      if (isStackManaged(element)) {
        return;
      }

      prepareElement(element);

      if (observer) {
        observer.unobserve(element);

        if (!element.classList.contains('is-in-view')) {
          observer.observe(element);
        }
      }
    });
  }

  function handleResize() {
    clearTimeout(resizeTimer);

    resizeTimer = setTimeout(() => {
      rebuildAll();
    }, 180);
  }

  function observeDynamicRevealNodes() {
    if (!document.body || !('MutationObserver' in window) || dynamicRevealObserver) return;

    dynamicRevealObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        mutation.addedNodes.forEach((node) => {
          if (!(node instanceof Element)) return;

          if (node.matches(SELECTOR)) {
            initReveal(node);
            return;
          }

          if (node.querySelector && node.querySelector(SELECTOR)) {
            initReveal(node);
          }
        });
      });
    });

    dynamicRevealObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  function refreshAfterFontsLoad() {
    if (fontsRefreshDone) return;
    if (!document.fonts || !document.fonts.ready) return;

    fontsRefreshDone = true;

    document.fonts.ready
      .then(() => {
        rebuildAll();
      })
      .catch(() => {});
  }

  function start() {
    initReveal(document);
    observeDynamicRevealNodes();
    refreshAfterFontsLoad();
  }

  window.ctRefreshReveal = refreshRevealScope;
  window.ctInitReveal = initReveal;

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  window.addEventListener('resize', handleResize);

  document.addEventListener('shopify:section:load', (event) => {
    window.requestAnimationFrame(() => {
      initReveal(event.target, { force: true });
    });
  });

  document.addEventListener('shopify:block:select', (event) => {
    window.requestAnimationFrame(() => {
      initReveal(event.target, { force: true });
    });
  });
})();

/* Keep the cart icon in sync when Safari restores a page from back/forward cache. */
(() => {
  let cartIconRefreshInFlight = null;

  const refreshCartIcon = () => {
    const cartIcon = document.getElementById('cart-icon-bubble');
    if (!cartIcon || !window.routes?.cart_url) return Promise.resolve();

    if (cartIconRefreshInFlight) return cartIconRefreshInFlight;

    const separator = window.routes.cart_url.includes('?') ? '&' : '?';
    const url = `${window.routes.cart_url}${separator}section_id=cart-icon-bubble&_=${Date.now()}`;

    cartIconRefreshInFlight = fetch(url, {
      credentials: 'same-origin',
      cache: 'no-store',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    })
      .then((response) => {
        if (!response.ok) throw new Error(`Cart icon refresh failed: ${response.status}`);
        return response.text();
      })
      .then((html) => {
        const parsed = new DOMParser().parseFromString(html, 'text/html');
        const section = parsed.querySelector('.shopify-section');
        if (!section) return;

        cartIcon.innerHTML = section.innerHTML;
      })
      .catch((error) => console.error(error))
      .finally(() => {
        cartIconRefreshInFlight = null;
      });

    return cartIconRefreshInFlight;
  };

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartIcon();
  });
})();
